/**
 * Canvas-2D port of the procedural dweller sprites (src/game/sprites.ts).
 *
 * The landing page must not import Phaser (it would triple the public
 * bundle), so the exact same pixel-drawing routine is reproduced here with
 * CanvasRenderingContext2D. Keep body geometry, palettes and headwear in
 * sync with src/game/sprites.ts — these ARE the real agent designs.
 */
import { PALETTE_HEX } from '../../theme';
import type { SpriteKey } from '../../types';

export const FRAME_W = 24;
export const FRAME_H = 32;
export const IDLE_FRAME_COUNT = 4;
export const WALK_FRAME_COUNT = 8;

interface DwellerPalette {
  outfit: string;
  accent: string;
  shoes: string;
}

// Mirrors DWELLER_PALETTES in src/game/sprites.ts (hex-string form).
const DWELLER_PALETTES: Record<SpriteKey, DwellerPalette> = {
  overseer:     { outfit: PALETTE_HEX.overseer,     accent: '#d4d4e8', shoes: '#1a1a2e' },
  elder:        { outfit: PALETTE_HEX.elder,        accent: '#c8c8c8', shoes: '#2a1a0e' },
  scout:        { outfit: PALETTE_HEX.scout,        accent: PALETTE_HEX.terminal, shoes: '#1a1a2e' },
  investigator: { outfit: PALETTE_HEX.investigator, accent: '#e8e0f0', shoes: '#1a1a2e' },
  scribe:       { outfit: PALETTE_HEX.scribe,       accent: '#d0d8e0', shoes: '#0d1a20' },
  analyst:      { outfit: PALETTE_HEX.analyst,      accent: '#e0f8ff', shoes: '#0a1a1a' },
  tinkerer:     { outfit: PALETTE_HEX.tinkerer,     accent: '#ffd9a8', shoes: '#2a1408' },
  auditor:      { outfit: PALETTE_HEX.auditor,      accent: '#ffe0e0', shoes: '#1a0808' },
  designer:     { outfit: PALETTE_HEX.designer,     accent: '#fff0c0', shoes: '#2a2010' },
  caravaneer:   { outfit: PALETTE_HEX.caravaneer,   accent: '#ffd0a8', shoes: '#2a1408' },
};

const OUTLINE = '#0a0a14';

/** Multiply a #rrggbb colour by a factor (clamped) — same ramp as sprites.ts. */
function shade(hex: string, f: number): string {
  const h = hex.replace('#', '');
  const ch = (i: number): string =>
    Math.min(255, Math.floor(parseInt(h.slice(i, i + 2), 16) * f))
      .toString(16)
      .padStart(2, '0');
  return `#${ch(0)}${ch(2)}${ch(4)}`;
}

function rect(ctx: CanvasRenderingContext2D, color: string, x: number, y: number, w: number, h: number): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

function drawArm(ctx: CanvasRenderingContext2D, x: number, topY: number, p: DwellerPalette): void {
  rect(ctx, OUTLINE, x - 1, topY - 1, 4, 11);
  rect(ctx, p.outfit, x, topY, 2, 7);
  rect(ctx, shade(p.outfit, 0.68), x + 1, topY, 1, 7);
  rect(ctx, PALETTE_HEX.skin, x, topY + 7, 2, 2);
}

function drawIdleLegs(ctx: CanvasRenderingContext2D, p: DwellerPalette, cx: number): void {
  rect(ctx, OUTLINE, cx - 5, 25, 4, 7);
  rect(ctx, OUTLINE, cx, 25, 4, 7);
  rect(ctx, p.shoes, cx - 4, 26, 3, 5);
  rect(ctx, p.shoes, cx + 1, 26, 3, 5);
  rect(ctx, shade(p.shoes, 1.6), cx - 4, 26, 3, 1);
  rect(ctx, shade(p.shoes, 1.6), cx + 1, 26, 3, 1);
}

const WALK_LEG_OFFSETS: Array<[number, number, number, number]> = [
  [0, 0, 0, 0],
  [2, 1, -1, 0],
  [3, 2, -2, 0],
  [2, 1, -1, 0],
];

function drawWalkLegs(
  ctx: CanvasRenderingContext2D,
  p: DwellerPalette,
  cx: number,
  walkPhase: number,
  rightLeads: boolean
): void {
  const [ld, ly, td, ty] = WALK_LEG_OFFSETS[walkPhase];
  const drawLeg = (lx: number, lyy: number): void => {
    rect(ctx, OUTLINE, lx - 1, 25 + lyy, 5, 7);
    rect(ctx, p.shoes, lx, 26 + lyy, 3, 5);
    rect(ctx, shade(p.shoes, 1.6), lx, 26 + lyy, 3, 1);
  };
  if (rightLeads) {
    drawLeg(cx + 1 + ld, ly);
    drawLeg(cx - 4 - td, ty);
  } else {
    drawLeg(cx - 4 - ld, ly);
    drawLeg(cx + 1 + td, ty);
  }
}

function drawBody(
  ctx: CanvasRenderingContext2D,
  p: DwellerPalette,
  cx: number,
  bodyY: number,
  walking: boolean,
  walkPhase: number,
  rightLeads = true
): void {
  const oL = shade(p.outfit, 1.3);
  const oD = shade(p.outfit, 0.6);
  const skin = PALETTE_HEX.skin;
  const skinL = shade(skin, 1.15);
  const skinD = shade(skin, 0.72);

  // Contact shadow
  ctx.fillStyle = 'rgba(0,0,0,0.28)';
  ctx.beginPath();
  ctx.ellipse(cx, 31, 8.5, 2, 0, 0, Math.PI * 2);
  ctx.fill();

  if (walking) {
    drawWalkLegs(ctx, p, cx, walkPhase, rightLeads);
  } else {
    drawIdleLegs(ctx, p, cx);
  }

  if (walking) {
    const swing = walkPhase === 2 ? 2 : walkPhase === 0 ? 0 : 1;
    if (rightLeads) {
      drawArm(ctx, cx - 7, 17 + bodyY - swing, p);
      drawArm(ctx, cx + 5, 17 + bodyY + swing, p);
    } else {
      drawArm(ctx, cx - 7, 17 + bodyY + swing, p);
      drawArm(ctx, cx + 5, 17 + bodyY - swing, p);
    }
  } else {
    drawArm(ctx, cx - 7, 17 + bodyY, p);
    drawArm(ctx, cx + 5, 17 + bodyY, p);
  }

  // Torso
  rect(ctx, OUTLINE, cx - 6, 15 + bodyY, 12, 12);
  rect(ctx, p.outfit, cx - 5, 16 + bodyY, 10, 10);
  rect(ctx, oL, cx - 5, 16 + bodyY, 2, 10);
  rect(ctx, oD, cx + 3, 16 + bodyY, 2, 10);
  rect(ctx, oD, cx - 5, 24 + bodyY, 10, 2);
  rect(ctx, p.accent, cx - 3, 16 + bodyY, 6, 2);

  // Head
  rect(ctx, OUTLINE, cx - 5, 7 + bodyY, 10, 9);
  rect(ctx, skin, cx - 4, 8 + bodyY, 8, 8);
  rect(ctx, skinL, cx - 4, 8 + bodyY, 2, 7);
  rect(ctx, skinD, cx + 2, 8 + bodyY, 2, 7);
  rect(ctx, skinD, cx - 4, 14 + bodyY, 8, 2);
}

function drawEyes(ctx: CanvasRenderingContext2D, cx: number, breath: number, blink: boolean): void {
  if (blink) {
    rect(ctx, '#1a1a2e', cx - 3, 13 + breath, 2, 1);
    rect(ctx, '#1a1a2e', cx + 1, 13 + breath, 2, 1);
  } else {
    rect(ctx, '#1a1a2e', cx - 3, 12 + breath, 2, 2);
    rect(ctx, '#1a1a2e', cx + 1, 12 + breath, 2, 2);
  }
}

function drawHeadwear(ctx: CanvasRenderingContext2D, key: SpriteKey, p: DwellerPalette, cx: number, breath: number): void {
  if (key === 'overseer') {
    rect(ctx, p.outfit, cx - 5, 5 + breath, 10, 4);
    rect(ctx, p.accent, cx - 6, 8 + breath, 12, 1);
    rect(ctx, '#ffd700', cx - 1, 6 + breath, 2, 2);
    return;
  }
  if (key === 'elder') {
    rect(ctx, p.outfit, cx - 6, 5 + breath, 12, 5);
    rect(ctx, p.accent, cx - 3, 14 + breath, 6, 3);
    rect(ctx, p.accent, cx - 4, 15 + breath, 1, 1);
    rect(ctx, p.accent, cx + 3, 15 + breath, 1, 1);
    return;
  }
  if (key === 'scout') {
    rect(ctx, p.outfit, cx - 5, 5 + breath, 10, 5);
    rect(ctx, p.accent, cx - 1, Math.max(0, breath), 2, 5);
    rect(ctx, p.accent, cx, Math.max(0, -2 + breath), 1, 2);
    rect(ctx, p.accent, cx - 4, 11 + breath, 8, 1);
    return;
  }
  if (key === 'investigator') {
    rect(ctx, p.outfit, cx - 4, 5 + breath, 8, 3);
    rect(ctx, '#5a3070', cx - 6, 7 + breath, 12, 1);
    rect(ctx, '#f5f5dc', cx + 5, 18 + breath, 3, 4);
    rect(ctx, '#2a1a40', cx + 6, 19 + breath, 1, 2);
    return;
  }
  if (key === 'scribe') {
    rect(ctx, p.outfit, cx - 5, 5 + breath, 10, 4);
    rect(ctx, '#1a1a2e', cx - 4, 11 + breath, 3, 2);
    rect(ctx, '#1a1a2e', cx + 1, 11 + breath, 3, 2);
    rect(ctx, '#1a1a2e', cx - 1, 12 + breath, 2, 1);
    rect(ctx, '#4a90d9', cx - 8, 19 + breath, 2, 4);
    return;
  }
  if (key === 'analyst') {
    rect(ctx, p.outfit, cx - 5, 5 + breath, 10, 4);
    rect(ctx, '#00e5ff', cx - 4, 8 + breath, 8, 2);
    rect(ctx, '#00bcd4', cx - 8, 21 + breath, 3, 3);
    rect(ctx, '#00e5ff', cx - 7, 22 + breath, 1, 1);
    return;
  }
  if (key === 'tinkerer') {
    rect(ctx, p.outfit, cx - 5, 5 + breath, 10, 6);
    rect(ctx, '#1a1a1a', cx - 4, 9 + breath, 8, 2);
    rect(ctx, p.accent, cx - 3, 9 + breath, 1, 2);
    rect(ctx, p.accent, cx + 2, 9 + breath, 1, 2);
    rect(ctx, '#6a4a2a', cx + 5, 19 + breath, 1, 4);
    rect(ctx, '#808080', cx + 4, 18 + breath, 3, 2);
    return;
  }
  if (key === 'auditor') {
    rect(ctx, p.outfit, cx - 5, 5 + breath, 10, 4);
    rect(ctx, '#1a1a1a', cx + 1, 11 + breath, 4, 1);
    rect(ctx, '#1a1a1a', cx + 1, 13 + breath, 4, 1);
    rect(ctx, '#1a1a1a', cx + 1, 12 + breath, 1, 1);
    rect(ctx, '#1a1a1a', cx + 4, 12 + breath, 1, 1);
    rect(ctx, p.accent, cx + 2, 12 + breath, 2, 1);
    rect(ctx, '#d0c0a0', cx - 8, 18 + breath, 3, 5);
    rect(ctx, '#4a3a2a', cx - 7, 19 + breath, 1, 1);
    rect(ctx, '#4a3a2a', cx - 7, 21 + breath, 1, 1);
    return;
  }
  if (key === 'designer') {
    rect(ctx, p.outfit, cx - 5, 6 + breath, 10, 3);
    rect(ctx, p.outfit, cx - 4, 4 + breath, 8, 3);
    rect(ctx, p.accent, cx + 3, 4 + breath, 2, 1);
    rect(ctx, '#e8e0c0', cx - 8, 18 + breath, 4, 5);
    rect(ctx, '#2a2a3e', cx - 7, 19 + breath, 2, 1);
    rect(ctx, '#2a2a3e', cx - 7, 21 + breath, 1, 1);
    return;
  }
  if (key === 'caravaneer') {
    rect(ctx, p.outfit, cx - 5, 5 + breath, 10, 4);
    rect(ctx, '#4a2a14', cx - 6, 8 + breath, 12, 1);
    rect(ctx, p.accent, cx - 1, 6 + breath, 2, 1);
    rect(ctx, '#e6d4a8', cx + 5, 18 + breath, 3, 5);
    rect(ctx, '#8a6a3a', cx + 5, 18 + breath, 3, 1);
    rect(ctx, '#8a6a3a', cx + 5, 22 + breath, 3, 1);
    return;
  }
}

export interface DwellerFrames {
  /** 4 breathing frames; frame 2 blinks. */
  idle: HTMLCanvasElement[];
  /** 8 walk-cycle frames (right leg leads 0-3, left leads 4-7). */
  walk: HTMLCanvasElement[];
}

const frameCache = new Map<SpriteKey, DwellerFrames>();

function makeFrame(draw: (ctx: CanvasRenderingContext2D) => void): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = FRAME_W;
  canvas.height = FRAME_H;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  draw(ctx);
  return canvas;
}

export function getDwellerFrames(key: SpriteKey): DwellerFrames {
  const cached = frameCache.get(key);
  if (cached) return cached;
  const p = DWELLER_PALETTES[key];

  const idle = Array.from({ length: IDLE_FRAME_COUNT }, (_, frame) =>
    makeFrame((ctx) => {
      const breath = frame === 1 ? -1 : frame === 3 ? 1 : 0;
      drawBody(ctx, p, FRAME_W / 2, breath, false, 0);
      drawHeadwear(ctx, key, p, FRAME_W / 2, breath);
      drawEyes(ctx, FRAME_W / 2, breath, frame === 2);
    })
  );

  const walk = Array.from({ length: WALK_FRAME_COUNT }, (_, frame) =>
    makeFrame((ctx) => {
      const phase = frame % 4;
      const bodyY = phase === 0 ? 1 : phase === 2 ? -1 : 0;
      const walkPhase = frame < 4 ? frame : frame - 4;
      const rightLeads = frame < 4;
      drawBody(ctx, p, FRAME_W / 2, bodyY, true, walkPhase, rightLeads);
      drawHeadwear(ctx, key, p, FRAME_W / 2, bodyY);
      drawEyes(ctx, FRAME_W / 2, bodyY, false);
    })
  );

  const frames = { idle, walk };
  frameCache.set(key, frames);
  return frames;
}
